import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';
import translations from '../translations';
import sendEmail from '../services/emailService';

const ContactSection = styled.section`
  padding: 100px 0;
  background-color: ${props => props.theme === 'dark' ? '#1e1e1e' : '#f8f9fa'};
`;

const ContactContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 992px) {
    flex-direction: row;
    gap: 60px;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
  margin-bottom: 40px;
  
  @media (min-width: 992px) {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 1.5rem;
`;

const SectionDescription = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ContactDetails = styled.div`
  margin-top: 2rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: ${props => props.theme === 'dark' ? '#fff' : '#23272f'};
  font-size: 1.2rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.04);
  border: 1.5px solid ${props => props.theme === 'dark' ? '#23272f' : '#fff'};
  transition: background 0.3s, color 0.3s;
`;

const ContactText = styled.div`
  flex: 1;
`;

const ContactLabel = styled.h3`
  font-size: 1.1rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 0.5rem;
`;

const ContactValue = styled.p`
  font-size: 1rem;
  color: ${props => props.theme === 'dark' ? '#ced4da' : '#6c757d'};
`;

const FormWrapper = styled.div`
  flex: 1;
`;

const ContactForm = styled.form`
  background-color: ${props => props.theme === 'dark' ? '#121212' : '#ffffff'};
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, ${props => props.theme === 'dark' ? '0.3' : '0.05'});
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 1rem;
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${props => props.theme === 'dark' ? '#343a40' : '#ced4da'};
  border-radius: 8px;
  background-color: ${props => props.theme === 'dark' ? '#343a40' : '#ffffff'};
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4a6cf7;
    box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 15px;
  border: 1px solid ${props => props.theme === 'dark' ? '#343a40' : '#ced4da'};
  border-radius: 8px;
  background-color: ${props => props.theme === 'dark' ? '#343a40' : '#ffffff'};
  color: ${props => props.theme === 'dark' ? '#f8f9fa' : '#333'};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #4a6cf7;
    box-shadow: 0 0 0 3px rgba(74, 108, 247, 0.2);
  }
`;

const SubmitButton = styled(motion.button)`
  background-color: #4a6cf7;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  border: none;
  outline: none;
  box-shadow: 0 4px 6px rgba(74, 108, 247, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #3a56d4;
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(74, 108, 247, 0.3);
  }
  
  &:disabled {
    background-color: ${props => props.theme === 'dark' ? '#343a40' : '#ced4da'};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const Alert = styled.div`
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 1rem;
  background-color: ${props => props.success ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)'};
  color: ${props => props.success ? '#28a745' : '#dc3545'};
  border: 1px solid ${props => props.success ? '#28a745' : '#dc3545'};
`;

const Contact = ({ theme }) => {
  const { language } = useLanguage();
  const t = translations.contact[language];
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({
    show: false,
    success: false,
    message: ''
  });
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validierung
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setAlert({
        show: true,
        success: false,
        message: language === 'de' ? 
          'Bitte füllen Sie alle Pflichtfelder aus.' : 
          'Please fill in all required fields.'
      });
      return;
    }
    
    // E-Mail-Validierung
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setAlert({
        show: true,
        success: false,
        message: language === 'de' ? 
          'Bitte geben Sie eine gültige E-Mail-Adresse ein.' : 
          'Please enter a valid email address.'
      });
      return;
    }
    
    setIsSubmitting(true);
    setAlert({ show: false, success: false, message: '' });
    
    try {
      // Sende E-Mail über den E-Mail-Service
      const response = await sendEmail(formData);
      
      setAlert({
        show: true,
        success: true,
        message: t.successMessage
      });
      
      // Formular zurücksetzen
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      setAlert({
        show: true,
        success: false,
        message: language === 'de' ? 
          'Es gab ein Problem beim Senden der Nachricht. Bitte versuchen Sie es später erneut.' : 
          'There was a problem sending your message. Please try again later.'
      });
      console.error('Error sending email:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContactSection theme={theme} id="contact">
      <ContactContainer>
        <ContactInfo>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <SectionTitle theme={theme}>{t.title}</SectionTitle>
            <SectionDescription theme={theme}>
              {t.subtitle}
            </SectionDescription>
          </motion.div>
          
          <ContactDetails>
            <ContactItem>
              <ContactIcon>📧</ContactIcon>
              <ContactText>
                <ContactLabel theme={theme}>{t.emailLabel}</ContactLabel>
                <ContactValue theme={theme}>office@companion.at</ContactValue>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>🏢</ContactIcon>
              <ContactText>
                <ContactLabel theme={theme}>{t.addressLabel}</ContactLabel>
                <ContactValue theme={theme}>
                  Johann Nepomuk Berger-Strasse 7/47<br />
                  7210 Mattersburg<br />
                  Österreich
                </ContactValue>
              </ContactText>
            </ContactItem>
          </ContactDetails>
        </ContactInfo>
        
        <FormWrapper>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <ContactForm theme={theme} onSubmit={handleSubmit}>
              {alert.show && (
                <Alert success={alert.success}>
                  {alert.message}
                </Alert>
              )}
              
              <FormGroup>
                <FormLabel theme={theme}>{t.name}</FormLabel>
                <FormInput 
                  type="text" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  required
                  theme={theme}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel theme={theme}>{t.email}</FormLabel>
                <FormInput 
                  type="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  required
                  theme={theme}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel theme={theme}>{t.subject}</FormLabel>
                <FormInput 
                  type="text" 
                  name="subject" 
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  theme={theme}
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel theme={theme}>{t.message}</FormLabel>
                <FormTextarea 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  required
                  theme={theme}
                />
              </FormGroup>
              
              <SubmitButton 
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.05 }}
                whileTap={{ scale: isSubmitting ? 1 : 0.95 }}
                theme={theme}
              >
                {isSubmitting ? t.sending : t.send}
              </SubmitButton>
            </ContactForm>
          </motion.div>
        </FormWrapper>
      </ContactContainer>
    </ContactSection>
  );
};

export default Contact;
